'use client';

/**
 * ToolTag — monospace pill used inside the AstorBlock tool-trace list.
 *
 * Renders compact tool identifiers like `credit.pull` or
 * `bank.parse_statements` in JetBrains-Mono-style monospace with a
 * neutral hairline border (default) or mint border (active).
 *
 * Visual DNA: matches `pages/loans.jsx` AstorBlock `tools.map(t => ...)`
 * mono pills. Companion to {@link CitationPill} (mint-tinted) — ToolTag
 * is neutral by default because it labels the tool inventory rather
 * than a citation back to a source.
 */

import { Box } from '@mui/material';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef, type MouseEvent, type ReactNode } from 'react';
import { useI18n } from '../../hooks';

export type ToolTagVariant = 'default' | 'active';
export type ToolTagSize = 'sm' | 'md';

export interface ToolTagProps {
  /** Tool identifier rendered inside the pill (e.g. `credit.pull`). */
  label: ReactNode;
  /**
   * Visual variant:
   *  - `default` — neutral surface, hairline border (inventory list).
   *  - `active`  — mint border + tint (the currently-running tool).
   * @default 'default'
   */
  variant?: ToolTagVariant;
  /**
   * Pill density. `sm` (default) matches the AstorBlock pill scale.
   */
  size?: ToolTagSize;
  /** Optional click handler — makes the pill a real button (focusable). */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** Optional aria-label override; falls back to "Tool: {label}". */
  ariaLabel?: string;
  sx?: SxProps<Theme>;
}

/**
 * Mono pill for the AstorBlock tool-trace list.
 */
export const ToolTag = forwardRef<HTMLElement, ToolTagProps>(function ToolTag(
  { label, variant = 'default', size = 'sm', onClick, ariaLabel, sx },
  ref,
) {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';

  const mint = theme.palette.success.main;
  const mintDim = alpha(mint, isDark ? 0.18 : 0.14);
  const mintBorder = alpha(mint, 0.38);

  const sizeStyles =
    size === 'md' ? { px: 1, py: 0.5, fontSize: '0.75rem' } : { px: 0.875, py: 0.25, fontSize: '0.6875rem' };

  const colors =
    variant === 'active'
      ? { bg: mintDim, color: mint, border: mintBorder }
      : { bg: 'transparent', color: 'text.secondary' as const, border: theme.palette.divider };

  const labelText = typeof label === 'string' ? label : '';
  const resolvedAria = ariaLabel ?? (labelText ? t('astor.toolTag.ariaLabel', { label: labelText }) : undefined);

  const baseSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
    backgroundColor: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
    borderRadius: 0.5,
    fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    ...sizeStyles,
  };

  const interactiveSx: SxProps<Theme> = onClick
    ? {
        cursor: 'pointer',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '&:hover': {
          borderColor: mintBorder,
          backgroundColor: alpha(mint, isDark ? 0.1 : 0.08),
        },
        '&:focus-visible': {
          outline: `2px solid ${mint}`,
          outlineOffset: 2,
        },
      }
    : {};

  if (onClick) {
    return (
      <Box
        ref={ref as React.Ref<HTMLButtonElement>}
        component="button"
        type="button"
        onClick={onClick}
        aria-label={resolvedAria}
        sx={[baseSx, interactiveSx, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        <Typography
          component="span"
          sx={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 1 }}
        >
          {label}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={ref as React.Ref<HTMLSpanElement>}
      component="span"
      role="status"
      aria-label={resolvedAria}
      sx={[baseSx, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Typography
        component="span"
        sx={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 1 }}
      >
        {label}
      </Typography>
    </Box>
  );
});

ToolTag.displayName = 'ToolTag';

export default ToolTag;
